import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip } from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { Link } from 'react-router-dom';
import AmbientBackground from '../common/AmbientBackground';
import { supabase } from '../../config/supabase';
import { useAuthStore } from '../../store/authStore';
import { getCardData } from '../../services/cardService';
import Button from '../common/Button';

// Register Chart.js components
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip);

interface InsightStats {
  totalReadings: number;
  commonCards: Array<{
    name: string;
    image_url: string | null;
    count: number;
    meaning_summary?: string | null;
    arcana_type?: string | null;
    suit?: string | null;
    themes?: string[] | null;
    shadow_themes?: string[] | null;
    traditional_imagery?: string | null;
  }>;
  commonThemes: Array<{
    name: string;
    color: string;
    count: number;
  }>;
  commonSubjects: Array<{
    subject: string;
    count: number;
  }>;
  commonSpread: {
    type: string;
    count: number;
  };
}

const ReadingInsights: React.FC = () => {
  const { user, initialized } = useAuthStore();
  const [stats, setStats] = useState<InsightStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedCard, setSelectedCard] = useState<InsightStats['commonCards'][0] | null>(null);
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    const fetchInsights = async () => {
      if (!initialized) {
        return;
      }

      if (!user) {
        setStats(null);
        setIsLoading(false);
        return;
      }

      try {
        // Verify session is still valid
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        if (sessionError || !session) {
          throw new Error('Session invalid or expired');
        }

        const { data: readings, error: readingsError } = await supabase
          .from('readings')
          .select('*')
          .eq('user_id', user.id);

        if (readingsError) throw readingsError;

        if (!readings || readings.length === 0) {
          setStats({
            totalReadings: 0,
            commonCards: [],
            commonThemes: [],
            commonSubjects: [],
            commonSpread: { type: '', count: 0 }
          });
          setIsLoading(false);
          return;
        }

        // Process readings to extract insights
        const cardCounts: { [key: string]: { count: number; image_url: string | null } } = {};
        const themeCounts: { [key: string]: { count: number; color: string } } = {};
        const subjectCounts: { [key: string]: number } = {};
        const spreadCounts: { [key: string]: number } = {};

        // Process all readings first
        readings.forEach(reading => {
          // Count cards
          reading.cards.forEach((card: any) => {
            const cardName = card.name;
            if (!cardCounts[cardName]) {
              cardCounts[cardName] = { count: 0, image_url: null };
            }
            cardCounts[cardName].count++;
          });

          // Count themes
          reading.themes.forEach((theme: any) => {
            if (!themeCounts[theme.name]) {
              themeCounts[theme.name] = { count: 0, color: theme.color };
            }
            themeCounts[theme.name].count++;
          });

          // Count subjects
          const subject = reading.subject;
          subjectCounts[subject] = (subjectCounts[subject] || 0) + 1;

          // Count spread types
          const spreadType = reading.spread_type;
          spreadCounts[spreadType] = (spreadCounts[spreadType] || 0) + 1;
        });

        // Get top 5 cards and fetch their data
        const topCards = Object.entries(cardCounts)
          .sort(([, a], [, b]) => b.count - a.count)
          .slice(0, 5);

        // Fetch card data for each top card
        const cardPromises = topCards.map(async ([name, data]) => {
          try {
            const cardData = await getCardData(name);
            return {
              name: cardData.card_name || name,
              image_url: cardData.image_url,
              count: data.count,
              meaning_summary: cardData.meaning_summary,
              arcana_type: cardData.arcana_type,
              suit: cardData.suit,
              themes: cardData.themes?.map(theme => theme.trim().replace(/[{}]/g, '')),
              shadow_themes: cardData.shadow_themes?.map(theme => theme.trim().replace(/[{}]/g, '')),
              traditional_imagery: cardData.traditional_imagery
            };
          } catch (error) {
            console.error(`Error fetching card data for ${name}:`, error);
            return {
              name,
              image_url: null,
              count: data.count
            };
          }
        });

        const commonCards = await Promise.all(cardPromises);

        const commonThemes = Object.entries(themeCounts)
          .map(([name, { count, color }]) => ({ name, count, color }))
          .sort((a, b) => b.count - a.count)
          .slice(0, 5);

        const commonSubjects = Object.entries(subjectCounts)
          .map(([subject, count]) => ({ subject, count }))
          .sort((a, b) => b.count - a.count)
          .slice(0, 5);

        const commonSpread = Object.entries(spreadCounts)
          .sort((a, b) => b[1] - a[1])[0];

        setStats({
          totalReadings: readings.length,
          commonCards,
          commonThemes,
          commonSubjects,
          commonSpread: {
            type: commonSpread[0],
            count: commonSpread[1]
          }
        });
        setError(null);

      } catch (err) {
        console.error('Error fetching insights:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch insights');
      } finally {
        setIsLoading(false);
      }
    };

    fetchInsights();
  }, [user, initialized]);

  // Add retry mechanism for loading failures
  useEffect(() => {
    if (error && retryCount < 3) {
      const timer = setTimeout(() => {
        console.log(`Retrying fetch insights (attempt ${retryCount + 1})...`);
        setIsLoading(true);
        setRetryCount(prev => prev + 1);
        // This will trigger the main useEffect to run again
      }, 2000); // Retry after 2 seconds

      return () => clearTimeout(timer);
    }
  }, [error, retryCount]);

  const formatSpreadType = (type: string) => {
    return type
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const handleRetry = () => {
    setRetryCount(0);
    setIsLoading(true);
    setError(null);
    // This will trigger the main useEffect to run again
  };

  // Chart configuration
  const spreadChartData = {
    labels: ['Single Card', 'Three Card', 'Five Card', 'Celtic Cross'],
    datasets: [{
      data: [
        stats?.commonSpread.type === '1-card' ? stats.commonSpread.count : 0,
        stats?.commonSpread.type === '3-card' ? stats.commonSpread.count : 0,
        stats?.commonSpread.type === '5-card' ? stats.commonSpread.count : 0,
        stats?.commonSpread.type === 'celtic-cross' ? stats.commonSpread.count : 0
      ],
      backgroundColor: [
        'rgba(147, 51, 234, 0.7)',  // Purple
        'rgba(245, 158, 11, 0.7)',  // Amber
        'rgba(79, 70, 229, 0.7)',   // Indigo
        'rgba(236, 72, 153, 0.7)'   // Pink
      ],
      borderColor: [
        'rgba(147, 51, 234, 1)',
        'rgba(245, 158, 11, 1)',
        'rgba(79, 70, 229, 1)',
        'rgba(236, 72, 153, 1)'
      ],
      borderWidth: 2,
      borderRadius: 6,
      maxBarThickness: 50
    }]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        backgroundColor: 'rgba(255, 255, 255, 0.9)',
        titleColor: '#1a1a1a',
        bodyColor: '#1a1a1a',
        borderColor: 'rgba(147, 51, 234, 0.2)',
        borderWidth: 1,
        padding: 10,
        displayColors: false,
        callbacks: {
          label: (context: any) => {
            const value = context.raw;
            return `${value} reading${value !== 1 ? 's' : ''}`;
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          stepSize: 1,
          color: document.documentElement.classList.contains('dark') ? '#e5e7eb' : '#374151'
        },
        grid: {
          color: 'rgba(147, 51, 234, 0.1)'
        }
      },
      x: {
        ticks: {
          color: document.documentElement.classList.contains('dark') ? '#e5e7eb' : '#374151'
        },
        grid: {
          display: false
        }
      }
    }
  };

  if (!initialized) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sun"></div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sun"></div>
      </div>
    );
  }

  if (error) {
    return (
      <AmbientBackground mood="sun" backgroundImage="/bg-still-dusk.png">
        <div className="max-w-4xl mx-auto">
          <div className="bg-parchment/60 dark:bg-shadow/60 border border-mist dark:border-dusk rounded-xl p-6">
            <div className="flex flex-col items-center py-8">
              <span className="text-sun text-4xl mb-4">!</span>
              <h2 className="text-xl font-display font-semibold text-ink dark:text-parchment mb-2">Error Loading Insights</h2>
              <p className="text-ink/70 dark:text-parchment/60 text-center mb-6">{error}</p>
              <Button variant="primary" onClick={handleRetry}>
                Try Again
              </Button>
              {retryCount > 0 && (
                <p className="mt-2 text-sm text-ink/50">
                  Retry attempt: {retryCount}/3
                </p>
              )}
            </div>
          </div>
        </div>
      </AmbientBackground>
    );
  }

  if (!user) {
    return (
      <AmbientBackground mood="sun" backgroundImage="/bg-still-dusk.png">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="max-w-4xl mx-auto"
        >
          <div className="bg-parchment/60 dark:bg-shadow/60 border border-mist dark:border-dusk rounded-xl p-8">
            <div className="text-center space-y-6">
              <h2 className="text-2xl font-display font-semibold text-ink dark:text-parchment">
                Please Log In to View Insights
              </h2>
              <p className="text-lg text-ink dark:text-parchment/60 max-w-lg mx-auto">
                You need to be logged in to view your reading insights.
              </p>
              <div className="flex justify-center gap-4">
                <Button
                  variant="outline"
                  onClick={() => window.location.href = '/login'}
                >
                  Log In
                </Button>
                <Button
                  variant="primary"
                  onClick={() => window.location.href = '/signup'}
                >
                  Sign Up
                </Button>
              </div>
            </div>
          </div>
        </motion.div>
      </AmbientBackground>
    );
  }

  if (!stats || stats.totalReadings === 0) {
    return (
      <AmbientBackground mood="sun" backgroundImage="/bg-still-dusk.png">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="max-w-4xl mx-auto"
        >
          <div className="bg-parchment/60 dark:bg-shadow/60 border border-mist dark:border-dusk rounded-xl p-8">
            <div className="text-center space-y-6">
              <span className="text-6xl text-sun dark:text-ember block mx-auto">✧</span>
              <h2 className="text-2xl font-display font-semibold text-ink dark:text-parchment">
                Your Tarot Journey Begins
              </h2>
              <p className="text-lg text-ink dark:text-parchment/60 max-w-lg mx-auto">
                As you save readings to your journal, this space will fill with insights about your tarot practice. Each reading adds to your unique story.
              </p>
              <div className="bg-parchment/60 dark:bg-shadow/60 rounded-xl p-6 border border-mist dark:border-dusk max-w-sm mx-auto">
                <span className="text-3xl text-sun dark:text-ember block mx-auto mb-4">✧</span>
                <p className="text-ink dark:text-parchment/60">
                  Start your journey by saving your first reading to your journal.
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      </AmbientBackground>
    );
  }

  return (
    <AmbientBackground mood="sun" backgroundImage="/bg-still-dusk.png">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="max-w-6xl mx-auto space-y-6"
      >
        <div className="bg-parchment/60 dark:bg-shadow/60 border border-mist dark:border-dusk rounded-xl p-6">
          <div className="flex flex-col md:flex-row md:items-center gap-4 mb-6">
            <div className="flex-grow">
              <h1 className="text-3xl md:text-4xl font-display font-semibold text-ink dark:text-parchment">
                Your Reading Journey
              </h1>
            </div>
            <div className="flex items-center justify-center bg-gradient-to-r from-sun to-parchment0 p-[2px] rounded-full">
              <div className="bg-vellum dark:bg-shadow rounded-full px-6 py-2">
                <span className="text-xl font-bold bg-gradient-to-r from-sun to-parchment0 bg-clip-text text-transparent">
                  {stats.totalReadings} Reading{stats.totalReadings !== 1 ? 's' : ''}
                </span>
              </div>
            </div>
          </div>

          <div className="space-y-8">
            {/* Most Common Cards */}
            <div>
              <h2 className="text-xl font-display font-semibold text-ink dark:text-parchment mb-4">
                Cards That Guide You Most
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
                {stats?.commonCards.map((card, index) => (
                  <div key={index} className="text-center">
                    <button
                      onClick={() => setSelectedCard(card)}
                      className="w-full group"
                    >
                      <div className="aspect-[2/3] bg-vellum dark:bg-shadow rounded-lg shadow-md overflow-hidden mb-2 transform transition-transform hover:scale-105">
                        {card.image_url ? (
                          <img
                            src={card.image_url}
                            alt={card.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center p-4">
                            <span className="text-sm font-medium text-ink/70 dark:text-parchment/60">
                              {card.name}
                            </span>
                          </div>
                        )}
                      </div>
                      <p className="text-sm font-medium text-ink dark:text-parchment">
                        {card.name}
                      </p>
                      <p className="text-xs text-sun dark:text-ember">
                        {card.count} time{card.count !== 1 ? 's' : ''}
                      </p>
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Common Themes */}
            <div>
              <h2 className="text-xl font-display font-semibold text-ink dark:text-parchment mb-4">
                Themes in Your Journey
              </h2>
              <div className="flex flex-wrap gap-3">
                {stats?.commonThemes.map((theme, index) => (
                  <div
                    key={index}
                    className={`${theme.color} px-4 py-2 rounded-full text-sm flex items-center gap-2`}
                  >
                    <span>{theme.name}</span>
                    <span className="bg-vellum/20 px-2 py-0.5 rounded-full text-xs">
                      {theme.count}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Reading Subjects */}
              <div className="bg-parchment/60 dark:bg-shadow/60 rounded-xl p-6 border border-mist dark:border-dusk">
                <h2 className="text-xl font-display font-semibold text-ink dark:text-parchment mb-4">
                  Reading Recipients
                </h2>
                <div className="space-y-3">
                  {stats?.commonSubjects.map((subject, index) => (
                    <div key={index} className="flex items-center justify-between">
                      <span className="text-ink dark:text-parchment/60">{subject.subject}</span>
                      <span className="text-sm text-sun dark:text-ember">
                        {subject.count} reading{subject.count !== 1 ? 's' : ''}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Favorite Spread Chart */}
              <div className="bg-parchment/60 dark:bg-shadow/60 rounded-xl p-6 border border-mist dark:border-dusk">
                <h2 className="text-xl font-display font-semibold text-ink dark:text-parchment mb-4">
                  Your Favorite Spreads
                </h2>
                <div className="h-[300px] relative">
                  <Bar data={spreadChartData} options={chartOptions} />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Navigation Links */}
        <div className="text-center space-x-4">
          <Link
            to="/center"
            className="text-sun dark:text-ember hover:text-sun dark:hover:text-ember"
          >
            Tarot Center
          </Link>
          <span className="text-mist">•</span>
          <Link
            to="/faq"
            className="text-sun dark:text-ember hover:text-sun dark:hover:text-ember"
          >
            About this App
          </Link>
          <span className="text-mist">•</span>
          <Link
            to="/guide/101"
            className="text-sun dark:text-ember hover:text-sun dark:hover:text-ember"
          >
            Tarot 101
          </Link>
        </div>

        {/* Card Details Modal */}
        {selectedCard && (
          <div
            className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
            onClick={() => setSelectedCard(null)}
          >
            <div
              className="relative max-w-2xl w-full bg-vellum dark:bg-shadow rounded-lg shadow-xl overflow-y-auto max-h-[90vh]"
              onClick={e => e.stopPropagation()}
            >
              <button
                onClick={() => setSelectedCard(null)}
                className="absolute top-4 right-4 text-ink/50 hover:text-ink dark:text-parchment/60 dark:hover:text-parchment"
              >
                <span className="text-2xl leading-none">×</span>
              </button>

              <div className="p-6">
                <div className="flex flex-col md:flex-row gap-6">
                  {/* Card Image */}
                  <div className="md:w-1/3">
                    {selectedCard.image_url && (
                      <img
                        src={selectedCard.image_url}
                        alt={selectedCard.name}
                        className="w-full rounded-lg shadow-lg"
                      />
                    )}
                    <div className="mt-4 text-center">
                      <h2 className="text-2xl font-display font-bold text-ink dark:text-parchment">
                        {selectedCard.name}
                      </h2>
                      <p className="text-sm text-sun dark:text-ember mt-1">
                        {selectedCard.arcana_type === 'major' ? 'Major Arcana' : `Minor Arcana • ${selectedCard.suit}`}
                      </p>
                      <p className="text-sm font-medium text-sun dark:text-ember mt-1">
                        Appears in {selectedCard.count} reading{selectedCard.count !== 1 ? 's' : ''}
                      </p>
                    </div>
                  </div>

                  {/* Card Details */}
                  <div className="md:w-2/3 space-y-6">
                    {selectedCard.meaning_summary && (
                      <div>
                        <h3 className="text-lg font-semibold text-shadow dark:text-parchment mb-2">
                          Card Meaning
                        </h3>
                        <div className="prose prose-gray dark:prose-invert max-w-none">
                          {selectedCard.meaning_summary.split('\n\n').map((paragraph, index) => (
                            <p key={index} className="text-ink dark:text-parchment/60 mb-4 last:mb-0">
                              {paragraph}
                            </p>
                          ))}
                        </div>
                      </div>
                    )}

                    {selectedCard.themes && selectedCard.themes.length > 0 && (
                      <div>
                        <h3 className="text-lg font-semibold text-shadow dark:text-parchment mb-2">
                          Themes
                        </h3>
                        <div className="flex flex-wrap gap-2">
                          {selectedCard.themes.map((theme, index) => (
                            <span
                              key={index}
                              className="px-3 py-1 bg-vellum dark:bg-shadow/30 text-sun dark:text-ember rounded-full text-sm"
                            >
                              {theme}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {selectedCard.shadow_themes && selectedCard.shadow_themes.length > 0 && (
                      <div>
                        <h3 className="text-lg font-semibold text-shadow dark:text-parchment mb-2">
                          Shadow Aspects
                        </h3>
                        <div className="flex flex-wrap gap-2">
                          {selectedCard.shadow_themes.map((theme, index) => (
                            <span
                              key={index}
                              className="px-3 py-1 bg-vellum dark:bg-shadow/30 text-shadow dark:text-ember rounded-full text-sm"
                            >
                              {theme}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {selectedCard.traditional_imagery && (
                      <div>
                        <h3 className="text-lg font-semibold text-shadow dark:text-parchment mb-2">
                          Traditional Imagery
                        </h3>
                        <div className="prose prose-gray dark:prose-invert max-w-none">
                          {selectedCard.traditional_imagery.split('\n\n').map((paragraph, index) => (
                            <p key={index} className="text-ink dark:text-parchment/60 mb-4 last:mb-0">
                              {paragraph}
                            </p>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </motion.div>
    </AmbientBackground>
  );
};

export default ReadingInsights;
